import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle, Circle, Loader2, XCircle, Clock, Sparkles } from 'lucide-react'

import { Progress } from '@/components/ui/progress'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useWebSocket, type GenerationProgress as WSGenerationProgress } from '@/hooks/useWebSocket'
import { articlesApi } from '@/services/api'
import { notify } from '@/stores/notificationStore'
import type { ArticleStatus } from '@/types'

interface LocalAgentProgress {
  agent: string
  status: string
  percent: number
  message: string
}

interface GenerationProgressProps {
  articleId: string
  initialStatus?: ArticleStatus
  initialProgress?: number
  onComplete?: () => void
  onError?: (error: string) => void
}

// Ordered list of stages for rendering (V3 pipeline)
const stageOrder = ['planning', 'writing', 'reviewing', 'generating_image'] as const

// Map individual agent names to V3 stage names
const AGENT_TO_STAGE: Record<string, string> = {
  strategist: 'planning',
  planner: 'planning',
  planning: 'planning',
  writer: 'writing',
  writing: 'writing',
  finalizer: 'reviewing',
  reviewer: 'reviewing',
  reviewing: 'reviewing',
  image_generator: 'generating_image',
  generating_image: 'generating_image',
}

const statusColors: Record<string, string> = {
  pending: 'text-muted-foreground',
  in_progress: 'text-blue-600',
  completed: 'text-green-600',
  failed: 'text-red-600',
}

export function GenerationProgress({
  articleId,
  initialStatus = 'pending',
  initialProgress = 0,
  onComplete,
  onError,
}: GenerationProgressProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<ArticleStatus>(initialStatus)
  const [progress, setProgress] = useState(initialProgress)
  const [currentAgent, setCurrentAgent] = useState<string | null>(null)
  const [agents, setAgents] = useState<LocalAgentProgress[]>([])
  const [estimatedTime, setEstimatedTime] = useState<string | null>(null)
  const [isLoadingProgress, setIsLoadingProgress] = useState(true)
  const [articleTitle, setArticleTitle] = useState<string>('')
  const notifiedRef = useRef(false)

  // Fetch current progress on mount to restore state
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const progressData = await articlesApi.getProgress(articleId)
        setStatus(progressData.status)
        setProgress(progressData.progress)
        setCurrentAgent(progressData.current_agent || null)

        // Convert API agents to local format
        if (progressData.agents && progressData.agents.length > 0) {
          const agentProgress: LocalAgentProgress[] = progressData.agents.map((a) => ({
            agent: a.agent,
            status: a.status,
            percent: a.percent,
            message: a.message || '',
          }))
          setAgents(agentProgress)
        }

        // Also fetch article details for title
        try {
          const articleData = await articlesApi.get(articleId)
          setArticleTitle(articleData.title || 'Article')
        } catch {
          // Ignore - title is optional for notifications
        }
      } catch (error) {
        console.error('Failed to fetch progress:', error)
      } finally {
        setIsLoadingProgress(false)
      }
    }

    fetchProgress()
  }, [articleId])

  const handleProgress = (data: WSGenerationProgress) => {
    // Normalize agent name to stage name for V3 pipeline
    const stageName = AGENT_TO_STAGE[data.agent] || data.agent
    const normalizedData = { ...data, agent: stageName }

    setProgress(data.percent)
    setCurrentAgent(stageName)
    setAgents((prev) => {
      const existing = prev.findIndex((a) => a.agent === stageName)
      if (existing >= 0) {
        const updated = [...prev]
        updated[existing] = normalizedData
        return updated
      }
      return [...prev, normalizedData]
    })
  }

  const handleComplete = () => {
    setStatus('ready')
    setProgress(100)

    // Send notification (only once)
    if (!notifiedRef.current) {
      notifiedRef.current = true
      notify.articleComplete(
        articleTitle || 'Your article',
        articleId,
        () => {
          // Navigate to article view - handled by parent component
          onComplete?.()
        }
      )
    }

    onComplete?.()
  }

  const handleFailed = (data: { error: string }) => {
    setStatus('failed')

    // Send notification (only once)
    if (!notifiedRef.current) {
      notifiedRef.current = true
      notify.articleFailed(
        articleTitle || 'Your article',
        data.error || 'Generation failed'
      )
    }

    onError?.(data.error || 'Generation failed')
  }

  const { lastMessage, isConnected } = useWebSocket({
    articleId,
    onProgress: handleProgress,
    onComplete: handleComplete,
    onFailed: handleFailed,
  })

  // Update estimated time from last message if available
  useEffect(() => {
    if (lastMessage?.type === 'generation_progress') {
      const data = lastMessage.data as { estimated_completion?: string }
      if (data.estimated_completion) {
        setEstimatedTime(data.estimated_completion)
      }
    }
  }, [lastMessage])

  const getStageStatus = (stageName: string): 'pending' | 'in_progress' | 'completed' | 'failed' => {
    const stage = agents.find((a) => a.agent === stageName)
    if (!stage) return 'pending'
    return stage.status as 'pending' | 'in_progress' | 'completed' | 'failed'
  }

  const getStageProgress = (stageName: string): number => {
    const stage = agents.find((a) => a.agent === stageName)
    return stage?.percent || 0
  }

  const getStatusIcon = (agentStatus: string) => {
    switch (agentStatus) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'in_progress':
        return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />
      default:
        return <Circle className="h-5 w-5 text-muted-foreground" />
    }
  }

  if (isLoadingProgress) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('generationProgress.loadingProgress')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stageOrder.slice(0, 3).map((key) => (
              <div key={key} className="h-16 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            {t('generationProgress.title')}
          </span>
          {!isConnected && (
            <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
              <Circle className="h-2 w-2 fill-yellow-500 text-yellow-500" />
              {t('generationProgress.reconnecting')}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{t('generationProgress.overallProgress')}</span>
            <span className="text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          {estimatedTime && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {t('generationProgress.estimatedCompletion')}: {new Date(estimatedTime).toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Pipeline Stages - V3 architecture */}
        <div className="space-y-3">
          {stageOrder.map((key) => {
            const stageStatus = getStageStatus(key)
            const stageProgress = getStageProgress(key)
            const isActive = currentAgent === key

            return (
              <div
                key={key}
                className={`p-3 rounded-lg border transition-colors ${
                  isActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-border'
                }`}
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(stageStatus)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`font-medium text-sm ${statusColors[stageStatus]}`}>
                        {t(`generationProgress.stages.${key}.label`)}
                      </p>
                      {stageStatus === 'in_progress' && (
                        <span className="text-xs text-blue-600">{stageProgress}%</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {t(`generationProgress.stages.${key}.description`)}
                    </p>
                  </div>
                </div>
                {stageStatus === 'in_progress' && (
                  <Progress value={stageProgress} className="h-1 mt-2" />
                )}
              </div>
            )
          })}
        </div>

        {/* Status Message */}
        {status === 'failed' && (
          <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">
              {t('generationProgress.generationFailed')}
            </p>
          </div>
        )}

        {status === 'ready' && (
          <div className="p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {t('generationProgress.generationComplete')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
